import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Win10AdminLayout from "../layouts/Win10AdminLayout";
import api from "../services/api";

const ROLE_OPTIONS = [
  { value: "tenant_admin", label: "Tenant admin" },
  { value: "eigenaar", label: "Eigenaar" },
  { value: "beheerder", label: "Beheerder" },
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  displayName: "",
  email: "",
  role: "tenant_admin",
  inviteMode: false,
  temporaryPassword: "",
};

function makeTempPassword() {
  const random = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `Tmp!${random.slice(0, 8)}A1`;
}

function mapBackendError(err) {
  const code = String(err?.response?.data?.code || "").trim();
  if (code === "FIRST_ADMIN_ALREADY_EXISTS") {
    return "Voor deze tenant bestaat al een hoofdgebruiker met adminrechten.";
  }
  if (code === "TENANT_NOT_FOUND") {
    return "Tenant niet gevonden.";
  }
  if (code === "INVALID_ADMIN_EMAIL") {
    return "E-mailadres is ongeldig.";
  }
  if (code === "INVALID_TEMP_PASSWORD") {
    return "Tijdelijk wachtwoord voldoet niet aan de eisen (minimaal 10 tekens, hoofdletter, kleine letter, cijfer, speciaal teken).";
  }
  return err?.response?.data?.message || "Eerste hoofdgebruiker aanmaken mislukt.";
}

export default function AdminTenantFirstAdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useParams();

  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdUser, setCreatedUser] = useState(null);

  const companyName = useMemo(() => {
    return String(location?.state?.companyName || "").trim();
  }, [location?.state?.companyName]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    setCreatedUser(null);

    const email = String(form.email || "").trim().toLowerCase();
    const role = String(form.role || "tenant_admin").trim();
    const inviteMode = form.inviteMode === true;
    const firstName = String(form.firstName || "").trim();
    const lastName = String(form.lastName || "").trim();
    const displayName = String(form.displayName || "").trim();
    const temporaryPassword = String(form.temporaryPassword || "");

    if (!email) {
      setError("E-mail is verplicht.");
      return;
    }

    if (!inviteMode && !temporaryPassword) {
      setError("Vul een tijdelijk wachtwoord in of schakel inviteMode in.");
      return;
    }

    const payload = {
      firstName,
      lastName,
      displayName,
      email,
      role,
      inviteMode,
    };
    if (!inviteMode) {
      payload.temporaryPassword = temporaryPassword;
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/admin/tenants/${tenantId}/first-admin`, payload);
      const user = res?.data?.user || null;
      setCreatedUser(user);
      setSuccess("Hoofdgebruiker aangemaakt.");
      setForm(INITIAL_FORM);
    } catch (err) {
      setError(mapBackendError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Win10AdminLayout>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Eerste hoofdgebruiker aanmaken</h1>
          <p className="text-sm text-slate-600">
            Tenant: <strong>{companyName || tenantId}</strong>
          </p>
          <p className="text-xs text-slate-500">Tenant ID: {tenantId}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/tenants")}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Later doen
          </button>
          <Link
            to="/admin/tenants"
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Terug naar tenants
          </Link>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 rounded border border-slate-300 bg-white p-5 shadow-sm md:grid-cols-2"
      >
        <Field label="Voornaam">
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => updateField("firstName", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <Field label="Achternaam">
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => updateField("lastName", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          />
        </Field>

        <Field label="Naam / displayName">
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => updateField("displayName", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
            placeholder="Bijv. Hoofdgebruiker"
          />
        </Field>

        <Field label="E-mail">
          <input
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
            required
          />
        </Field>

        <Field label="Rol">
          <select
            value={form.role}
            onChange={(e) => updateField("role", e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <label className="mt-7 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.inviteMode}
            onChange={(e) => updateField("inviteMode", e.target.checked)}
          />
          InviteMode (geen handmatig tijdelijk wachtwoord)
        </label>

        <div className="md:col-span-2">
          <Field label="Tijdelijk wachtwoord">
            <div className="flex gap-2">
              <input
                type="text"
                value={form.temporaryPassword}
                onChange={(e) => updateField("temporaryPassword", e.target.value)}
                disabled={form.inviteMode}
                className="w-full rounded border border-slate-300 px-3 py-2 disabled:bg-slate-100"
                placeholder="Minimaal 10 tekens met hoofdletter, kleine letter, cijfer, speciaal teken"
              />
              <button
                type="button"
                onClick={() => updateField("temporaryPassword", makeTempPassword())}
                disabled={form.inviteMode}
                className="whitespace-nowrap rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Genereer
              </button>
            </div>
          </Field>
        </div>

        <div className="md:col-span-2 flex items-center justify-end gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "Aanmaken..." : "Hoofdgebruiker aanmaken"}
          </button>
        </div>
      </form>

      {error ? <p className="mt-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      {success ? <p className="mt-3 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-700">{success}</p> : null}

      {createdUser ? (
        <div className="mt-4 rounded border border-slate-300 bg-white p-4">
          <h2 className="mb-2 text-base font-semibold text-slate-900">Aangemaakte hoofdgebruiker</h2>
          <p className="text-sm text-slate-700">Naam: {createdUser.naam || "-"}</p>
          <p className="text-sm text-slate-700">E-mail: {createdUser.email || "-"}</p>
          <div className="mt-3 flex gap-2">
            <Link
              to="/admin/tenants"
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Terug naar tenantoverzicht
            </Link>
          </div>
        </div>
      ) : null}
    </Win10AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
